import Header from "./components/Header";
import AccountSection from "./components/AccountSection";
import GreyLink from "./components/GreyLink";
import GoBack from "../components/GoBack";
import SplitColumns from "./components/SplitColumns";
import {useParams} from "react-router-dom";
import {useContext, useEffect, useState} from "react";
import Data from "../Data";
import ukMoment from "../../../utils/ukTime";
import Meta from "../../components/Meta";
import AppContext from "../../../context";
import {formatDuration} from "../utils/duration";

const Booking = () => {
    const context = useContext(AppContext);
    const isHybrid = context.domain?.businessType === 'hybrid' || context.domain?.businessType === 'hybrid-new';
    const formatBookingAmount = (amount) => isHybrid ? `${Number(amount || 0)} token${Number(amount || 0) === 1 ? '' : 's'}` : `£${amount}`;
    const { id } = useParams();
    const getQueryParam = (param) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }

    const success = getQueryParam('success');
    const paymentStatus = getQueryParam('paymentStatus');
    const paymentError = getQueryParam('errors');
    const errorRefNumber = getQueryParam('errorRefNumber');

    const isSumup = context.domain?.paymentProcessor === 'SumUp';
    const isWorldpay = context.domain?.paymentProcessor === 'Worldpay';
    const bundlePurchaseId = getQueryParam('bundlePurchase');

    const [booking, setBooking] = useState( null);
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const pollForPaid = async (bookingId, timeoutMs = 30000) => {
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
            const response = await Data.fetchExperienceBooking({ id: bookingId });
            if (response.data?.status === 'Paid') return response.data;
            await new Promise(resolve => setTimeout(resolve, 2500));
        }
        throw new Error('Payment verification timeout');
    };

    // Checks the booking's real payment status directly with the payment provider
    // (not just our own DB flag), retrying a few times in case of provider lag.
    // Importantly this runs even if the booking already shows "Paid" - that's
    // exactly the case where our own record turned out to be wrong.
    const recheckWithRetries = async (bookingId, attempts = 4, delayMs = 3000) => {
        let result = null;
        for (let attempt = 0; attempt < attempts; attempt++) {
            const response = await Data.recheckExperienceBookingPayment(bookingId);
            result = response.data;
            if (result?.status === 'paid') return result;
            if (attempt < attempts - 1) await new Promise(resolve => setTimeout(resolve, delayMs));
        }
        return result;
    };

    useEffect(async () => {
        try {
            const bookingResponse = await Data.fetchExperienceBooking({ id })
            let bookingData = bookingResponse.data
            setBooking(bookingData)

            if(success && bundlePurchaseId) {
                setVerifying(true)
                setVerifyError(false)
                try {
                    if(isSumup) await Data.verifySumupBundlePurchase(bundlePurchaseId)
                    // For Stripe and Worldpay, webhook confirms the booking — poll until it's paid
                    bookingData = await pollForPaid(id);
                    localStorage.removeItem('experience-cart');
                    setBooking(bookingData)
                } catch (error) {
                    setVerifyError(true)
                } finally {
                    setVerifying(false)
                }
            } else if(success) {
                setVerifying(true)
                setVerifyError(false)
                try {
                    const result = await recheckWithRetries(id);
                    const updatedBooking = result?.bookings?.[0];
                    if (updatedBooking) {
                        bookingData = updatedBooking;
                        setBooking(updatedBooking);
                        if (updatedBooking.status === 'Paid' || updatedBooking.status === 'Deposit Paid') {
                            localStorage.removeItem('experience-cart');
                        }
                    }
                } catch (error) {
                    setVerifyError(true)
                } finally {
                    setVerifying(false)
                }
            }
        } catch(error){
            window.location.href = '/admin/bookings'
        } finally {
            setLoading(false)
        }
    }, []);

    if(!booking && (paymentStatus === 'failed' || paymentError || errorRefNumber)) {
        return (
            <div className={"container mx-auto p-6"}>
                <div className={"bg-red-100 p-6 rounded-md border-red-300 border text-center"}>
                    <p className={"text-xl text-red-900"}>Payment failed</p>
                    <p className={"text-red-900 mt-1"}>{paymentError || 'We cannot currently process your request. Please contact support.'}</p>
                    {errorRefNumber ? (
                        <p className={"text-red-900 mt-1"}>Reference: {errorRefNumber}</p>
                    ) : null}
                    <div className={"mt-4"}>
                        <button
                            onClick={() => window.location.href = '/account'}
                            className={"border-0 bg-red-200 text-red-900 px-3 py-1.5 rounded-md hover:opacity-80 transition"}>Back to My Account
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if(!booking) return null

    const eventStart = new Date(booking.Event.date);
    const eventEnd = new Date(eventStart.getTime() + (Number(booking.Event.Experience?.duration) || 0) * 60000);
    const hasEventTakenPlace = eventEnd <= new Date();

    const refreshPaymentStatus = async () => {
        setRefreshing(true)
        try {
            const response = await Data.recheckExperienceBookingPayment(id)
            const updatedBooking = response.data?.bookings?.[0]
            if (updatedBooking) setBooking(updatedBooking)
        } catch(error) {
            window.alert("Could not verify payment status. Please try again or contact support.")
        } finally {
            setRefreshing(false)
        }
    }

    const cancel = async () => {
        setLoading(true)
        try {
            if(window.confirm('Are you sure you want to cancel this booking?')) {
                await Data.cancelExperienceBooking({ id })
                window.location.reload()
            }
        } catch(error){
            window.alert("There was an error cancelling your booking, please contact us.")
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <Meta title={`${booking.Event.Experience.name} (${booking.reference})`} />
            <GoBack to={"/account"} label={"Back to My Account"} />

            <Header heading={booking.reference} subHeading={'Booking Reference'} />

            {success && booking.status === 'Paid' ?
                <div className={"container mx-auto p-6 pt-0"}>
                    <div className={"bg-green-100 p-6 rounded-md border-green-300 border text-center"}>
                        <p className={"text-xl text-green-900"}>Thank you for your booking!</p>
                        <p className={"text-green-900 mt-1"}>We have sent you a confirmation email and we look forward to seeing you
                            on {ukMoment(booking.Event.date).format("Do MMMM")}.</p>
                    </div>
                </div>
                : null}

            {(paymentStatus === 'failed' || paymentError || errorRefNumber) ?
                <div className={"container mx-auto p-6 pt-0"}>
                    <div className={"bg-red-100 p-6 rounded-md border-red-300 border text-center"}>
                        <p className={"text-xl text-red-900"}>Payment failed</p>
                        <p className={"text-red-900 mt-1"}>{paymentError || 'We cannot currently process your request. Please contact support.'}</p>
                        {errorRefNumber ? (
                            <p className={"text-red-900 mt-1"}>Reference: {errorRefNumber}</p>
                        ) : null}
                        <div className={"mt-4"}>
                            <button
                                onClick={() => window.location.href = '/account'}
                                className={"border-0 bg-red-200 text-red-900 px-3 py-1.5 rounded-md hover:opacity-80 transition"}>Back to My Account
                            </button>
                        </div>
                    </div>
                </div>
                : null}

            {verifying ?
                <div className={"container mx-auto p-6 pt-0"}>
                    <div className={"bg-blue-100 p-6 rounded-md border-blue-300 border text-center"}>
                        <p className={"text-blue-900"}>Verifying your payment...</p>
                    </div>
                </div>
                : null}

            {verifyError ?
                <div className={"container mx-auto p-6 pt-0"}>
                    <div className={"bg-yellow-100 p-6 rounded-md border-yellow-300 border text-center"}>
                        <p className={"text-yellow-900"}>There was an issue verifying your payment. Please refresh or contact support.</p>
                    </div>
                </div>
                : null}

            {booking.status === 'Payment Pending' || booking.status === 'Payment Failed' ?
                <div className={"container mx-auto p-6 pt-0"}>
                    <div className={"bg-yellow-100 p-6 rounded-md border-yellow-300 border text-center"}>
                        <p className={"text-xl text-yellow-900"}>{booking.status === 'Payment Failed' ? 'Payment Not Confirmed' : 'Payment Pending'}</p>
                        <p className={"text-yellow-900 mt-1"}>Your payment has not been confirmed yet. If you have already paid, please click the button below to check again.</p>
                        <div className={"mt-4"}>
                            <button
                                onClick={refreshPaymentStatus}
                                disabled={refreshing}
                                className={"border-0 bg-yellow-200 text-yellow-900 px-3 py-1.5 rounded-md hover:opacity-80 transition disabled:opacity-50"}>
                                {refreshing ? 'Checking...' : 'Recheck Payment Status'}
                            </button>
                        </div>
                    </div>
                </div>
                : null}

            {booking.BundlePurchase ?
                <div className={"container mx-auto p-6 pt-0"}>
                    <div className={"bg-blue-100 p-6 rounded-md border-blue-300 border text-center"}>
                        <p className={"text-blue-900"}>This booking was made as part of a bundle.</p>
                    </div>
                </div>
                : null}

            {booking.status === "Cancelled" ?
                <div className={"container mx-auto p-6 pt-0"}>
                    <div className={"bg-red-100 p-4 rounded-md border-red-300 border text-center"}>
                        <p className={"text-red-900 mt-1"}>This booking was cancelled on {ukMoment(booking.cancelledOn).format("Do MMMM")}.</p>
                    </div>
                </div>
                : null}

            <div className={"container mx-auto p-6 pt-0 grid grid-cols-1 gap-4 lg:grid-cols-2"}>
                <AccountSection>
                <h3>Experience Details</h3>
                    <SplitColumns>
                        <p>Name</p>
                        <p>{booking.Event.Experience.name}</p>
                        <p>Date</p>
                        <p>{ukMoment(booking.Event.date).format("MMM Do YYYY")}</p>
                        <p>Time</p>
                        <p>{ukMoment(booking.Event.date).format("HH:mm A")}</p>
                        <p>Duration</p>
                        <p>{formatDuration(booking.Event.Experience.duration)}</p>
                        <p>Tickets</p>
                        <p>{booking.tickets}</p>
                        <p>Location</p>
                        <p>{booking.Event.Experience.location}</p>
                        {booking.access?.pin ? (
                            <>
                                <p>Access PIN</p>
                                <p>{booking.access.pin}</p>
                                <p>Access Window</p>
                                <p>{ukMoment(booking.access.start).format("MMM Do YYYY, HH:mm")} - {ukMoment(booking.access.end).format("HH:mm")}</p>
                            </>
                        ) : null}
                    </SplitColumns>
                </AccountSection>

                <AccountSection>
                    <h3>Booking Details</h3>
                    <SplitColumns>
                        <p>Status</p>
                        <p>{booking.status}</p>
                        {booking.BundlePurchase ? <>
                            <p>Purchased</p>
                            <p>as a bundle</p>
                        </> : null}
                        <p>Booked On</p>
                        <p>{ukMoment(booking.bookedOn).format("MMM Do YYYY, H:mm A")}</p>
                        <p>Booked By</p>
                        <p>{booking.User.firstName} {booking.User.lastName}</p>
                        <p>Booking Email</p>
                        <p>{booking.User.email}</p>
                        {booking.notes ? <>
                            <p>Notes</p>
                            <p className={"whitespace-pre-wrap"}>{booking.notes}</p>
                        </> : null}
                    </SplitColumns>
                </AccountSection>

                <AccountSection>
                    <h3>Payment Information</h3>
                    <SplitColumns>
                        <p>Ticket Price</p>
                        <p>{formatBookingAmount(booking.ticketPrice)} each</p>
                        <p>{isHybrid ? 'Tokens Used' : 'Total Amount'}</p>
                        <p>{formatBookingAmount(booking.totalPrice)}</p>
                        {booking.depositAmount ? <>
                            <p>Deposit Paid</p>
                            <p>{formatBookingAmount(booking.depositAmount)}</p>
                            <p>Balance Due</p>
                            <p>{formatBookingAmount((Number(booking.totalPrice) - Number(booking.depositAmount)).toFixed(2))}</p>
                        </> : null}
                    </SplitColumns>
                </AccountSection>

                {booking.status === "Paid" ? <AccountSection>
                    <h3>Cancellations</h3>
                    {booking.BundlePurchase ? (
                        <p className={"text-textGrey"}>This booking was made as part of a bundle and can not be individually cancelled. Contact us to cancel this booking.</p>
                    ) : hasEventTakenPlace ? (
                        <p className={"text-textGrey"}>This class has already taken place and can no longer be cancelled.</p>
                    ) : (
                        <>
                            <p className={"text-textGrey"}>This booking can be cancelled for a full refund until {booking.Event.Experience.refundWindow} hours prior to the experience date.</p>
                            <button
                                onClick={cancel}
                                className={"border-0 bg-red-200 text-red-900 px-2.5 py-1.5 rounded-md hover:opacity-80 transition"}>Cancel Booking
                            </button>
                        </>
                    )}
                </AccountSection> : null}
            </div>
        </>
    )
}

export default Booking
